import { useEffect, useRef } from 'react';
import { useViewer } from '../store/context';

/** The find-in-document bar. Navigation logic lives in useFind (App owns it). */
export function FindBar({ next, prev }: { next: () => void; prev: () => void }) {
  const findOpen = useViewer((s) => s.findOpen);
  const query = useViewer((s) => s.findQuery);
  const count = useViewer((s) => s.findCount);
  const current = useViewer((s) => s.findCurrent);
  const setFindQuery = useViewer((s) => s.setFindQuery);
  const setFindOpen = useViewer((s) => s.setFindOpen);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (findOpen) inputRef.current?.focus();
  }, [findOpen]);

  if (!findOpen) return null;

  return (
    <div className="mr-findbar absolute top-2 right-2 left-2 z-10 flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-2 py-1 shadow-lg sm:left-auto sm:right-4 dark:border-neutral-700 dark:bg-neutral-800">
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Find…"
        aria-label="Find in document"
        onChange={(e) => setFindQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) prev();
            else next();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setFindOpen(false);
          }
        }}
        className="min-w-0 flex-1 bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-500 sm:w-44 sm:flex-none dark:text-neutral-100 dark:placeholder:text-neutral-400"
      />
      <span className="min-w-16 text-right text-xs tabular-nums text-neutral-600 dark:text-neutral-300">
        {query.trim() === '' ? '' : `${String(current)}/${String(count)}`}
      </span>
      <button type="button" onClick={prev} aria-label="Previous match" className="mr-btn" disabled={count === 0}>
        ↑
      </button>
      <button type="button" onClick={next} aria-label="Next match" className="mr-btn" disabled={count === 0}>
        ↓
      </button>
      <button type="button" onClick={() => setFindOpen(false)} aria-label="Close find" className="mr-btn">
        ✕
      </button>
    </div>
  );
}
